// MathKeyboard.jsx
// Place in src/components/MathKeyboard.jsx
//
// A collapsible symbol picker panel that inserts LaTeX notation
// at the cursor position in a textarea. Works on both mobile and desktop.
//
// Usage in AgentDashboard.jsx:
//   import MathKeyboard from '../components/MathKeyboard';
//
//   // In state:
//   showMathKeyboard: false,
//
//   // In render, above the input row:
//   {this.state.showMathKeyboard && (
//     <MathKeyboard
//       textareaRef={this.textareaRef}
//       onInsert={(text) => {
//         this.setState(prev => ({ inputText: prev.inputText + text }));
//         this.textareaRef.current?.focus();
//       }}
//       onClose={() => this.setState({ showMathKeyboard: false })}
//     />
//   )}
//
//   // Toggle button in inputRow:
//   <button
//     onClick={() => this.setState(prev => ({ showMathKeyboard: !prev.showMathKeyboard }))}
//     style={S.mathToggleBtn(this.state.showMathKeyboard)}
//     title="Math keyboard"
//   >
//     ∑
//   </button>

import React, { useState } from 'react';

// ---- Symbol catalogue ----
// Each entry: { label: display, insert: LaTeX string to insert, title: tooltip }
// 'insert' is what gets placed in the textarea at cursor position.

const SYMBOL_CATEGORIES = [
  {
    name: 'Basic',
    symbols: [
      { label: '+',   insert: '+',          title: 'Plus' },
      { label: '−',   insert: '-',          title: 'Minus' },
      { label: '×',   insert: '\\times ',   title: 'Multiply' },
      { label: '÷',   insert: '\\div ',     title: 'Divide' },
      { label: '=',   insert: '=',          title: 'Equals' },
      { label: '≠',   insert: '\\neq ',     title: 'Not equal' },
      { label: '≈',   insert: '\\approx ',  title: 'Approximately' },
      { label: '≤',   insert: '\\leq ',     title: 'Less than or equal' },
      { label: '≥',   insert: '\\geq ',     title: 'Greater than or equal' },
      { label: '<',   insert: '<',          title: 'Less than' },
      { label: '>',   insert: '>',          title: 'Greater than' },
      { label: '±',   insert: '\\pm ',      title: 'Plus or minus' },
    ],
  },
  {
    name: 'Powers',
    symbols: [
      { label: 'x²',  insert: '^{2}',       title: 'Square (place cursor before)' },
      { label: 'x³',  insert: '^{3}',       title: 'Cube' },
      { label: 'xⁿ',  insert: '^{n}',       title: 'Power n' },
      { label: 'xᵃ',  insert: '^{}',        title: 'Custom power (type exponent)' },
      { label: 'x₁',  insert: '_{1}',       title: 'Subscript 1' },
      { label: 'xₙ',  insert: '_{n}',       title: 'Subscript n' },
      { label: 'x_{}',insert: '_{}',        title: 'Custom subscript' },
      { label: '√x',  insert: '\\sqrt{}',   title: 'Square root' },
      { label: '∛x',  insert: '\\sqrt[3]{}',title: 'Cube root' },
      { label: 'ⁿ√x', insert: '\\sqrt[n]{}',title: 'nth root' },
    ],
  },
  {
    name: 'Fractions',
    symbols: [
      { label: 'a/b', insert: '\\frac{}{}',  title: 'Fraction (numerator/denominator)' },
      { label: '½',   insert: '\\frac{1}{2}',title: 'One half' },
      { label: '¼',   insert: '\\frac{1}{4}',title: 'One quarter' },
      { label: '¾',   insert: '\\frac{3}{4}',title: 'Three quarters' },
      { label: '⅓',   insert: '\\frac{1}{3}',title: 'One third' },
      { label: '⅔',   insert: '\\frac{2}{3}',title: 'Two thirds' },
    ],
  },
  {
    name: 'Greek',
    symbols: [
      { label: 'π',   insert: '\\pi ',       title: 'Pi' },
      { label: 'θ',   insert: '\\theta ',    title: 'Theta' },
      { label: 'α',   insert: '\\alpha ',    title: 'Alpha' },
      { label: 'β',   insert: '\\beta ',     title: 'Beta' },
      { label: 'γ',   insert: '\\gamma ',    title: 'Gamma' },
      { label: 'Δ',   insert: '\\Delta ',    title: 'Delta (change)' },
      { label: 'δ',   insert: '\\delta ',    title: 'delta (small)' },
      { label: 'Σ',   insert: '\\Sigma ',    title: 'Sigma (sum)' },
      { label: 'σ',   insert: '\\sigma ',    title: 'sigma (small)' },
      { label: 'λ',   insert: '\\lambda ',   title: 'Lambda' },
      { label: 'μ',   insert: '\\mu ',       title: 'Mu' },
      { label: 'ω',   insert: '\\omega ',    title: 'Omega' },
      { label: 'φ',   insert: '\\phi ',      title: 'Phi' },
      { label: 'ρ',   insert: '\\rho ',      title: 'Rho' },
      { label: 'ε',   insert: '\\epsilon ',  title: 'Epsilon' },
    ],
  },
  {
    name: 'Calculus',
    symbols: [
      { label: '∫',   insert: '\\int ',                title: 'Integral' },
      { label: '∫ab', insert: '\\int_{a}^{b} ',        title: 'Definite integral' },
      { label: 'd/dx', insert: '\\frac{d}{dx}',        title: 'Derivative' },
      { label: '∂',   insert: '\\partial ',            title: 'Partial derivative' },
      { label: '∞',   insert: '\\infty ',              title: 'Infinity' },
      { label: 'lim', insert: '\\lim_{x \\to } ',      title: 'Limit' },
      { label: '→',   insert: '\\to ',                 title: 'Approaches / arrow' },
      { label: 'Σn',  insert: '\\sum_{n=1}^{\\infty}', title: 'Summation' },
    ],
  },
  {
    name: 'Geometry',
    symbols: [
      { label: '°',   insert: '^{\\circ}',   title: 'Degrees' },
      { label: '∠',   insert: '\\angle ',    title: 'Angle' },
      { label: '⊥',   insert: '\\perp ',     title: 'Perpendicular' },
      { label: '∥',   insert: '\\parallel ', title: 'Parallel' },
      { label: '≅',   insert: '\\cong ',     title: 'Congruent' },
      { label: '~',   insert: '\\sim ',      title: 'Similar' },
      { label: 'sin', insert: '\\sin ',      title: 'Sine' },
      { label: 'cos', insert: '\\cos ',      title: 'Cosine' },
      { label: 'tan', insert: '\\tan ',      title: 'Tangent' },
    ],
  },
  {
    name: 'Sets',
    symbols: [
      { label: '∈',   insert: '\\in ',       title: 'Element of' },
      { label: '∉',   insert: '\\notin ',    title: 'Not element of' },
      { label: '⊂',   insert: '\\subset ',   title: 'Subset' },
      { label: '⊃',   insert: '\\supset ',   title: 'Superset' },
      { label: '∪',   insert: '\\cup ',      title: 'Union' },
      { label: '∩',   insert: '\\cap ',      title: 'Intersection' },
      { label: '∅',   insert: '\\emptyset ', title: 'Empty set' },
      { label: 'ℝ',   insert: '\\mathbb{R}', title: 'Real numbers' },
      { label: 'ℤ',   insert: '\\mathbb{Z}', title: 'Integers' },
      { label: 'ℕ',   insert: '\\mathbb{N}', title: 'Natural numbers' },
    ],
  },
  {
    name: 'Chemistry',
    symbols: [
      { label: '→',   insert: '\\rightarrow ', title: 'Reaction arrow' },
      { label: '⇌',   insert: '\\rightleftharpoons ', title: 'Equilibrium' },
      { label: 'H₂O', insert: 'H_{2}O',        title: 'Water' },
      { label: 'CO₂', insert: 'CO_{2}',         title: 'Carbon dioxide' },
      { label: '°C',  insert: '^{\\circ}C',     title: 'Degrees Celsius' },
      { label: 'Δ',   insert: '\\Delta H',      title: 'Enthalpy change' },
      { label: 'mol', insert: '\\text{mol}',    title: 'Moles' },
      { label: '×10ⁿ',insert: '\\times 10^{}', title: 'Scientific notation' },
    ],
  },
];

// ---- Colour palette matching AgentDashboard ----
const ACCENT  = '#1a6fba';
const BG      = '#eef4fb';
const BG_H    = '#dce8f5';
const BORDER  = 'rgba(26,111,186,0.15)';
const TEXT_H  = '#0d2b4a';
const TEXT_MID = '#3a6080';
const FONT    = "'Segoe UI', 'Helvetica Neue', Arial, sans-serif";

export default function MathKeyboard({ textareaRef, onInsert, onClose }) {
  const [activeCategory, setActiveCategory] = useState('Basic');
  const [wrapInDollars, setWrapInDollars] = useState(true);

  const activeSymbols = SYMBOL_CATEGORIES.find(c => c.name === activeCategory)?.symbols || [];

  const handleInsert = (symbol) => {
    const textarea = textareaRef?.current;
    let toInsert = symbol.insert;

    // Optionally wrap in $ for inline math display
    if (wrapInDollars && toInsert !== '' && !toInsert.startsWith('\\text')) {
      // Only wrap if it's actual math, not punctuation
      const isMathSymbol = toInsert.startsWith('\\') || toInsert.includes('^') || toInsert.includes('_');
      if (isMathSymbol) {
        toInsert = `$${toInsert}$`;
      }
    }

    if (textarea) {
      // Insert at cursor position
      const start = textarea.selectionStart;
      const end   = textarea.selectionEnd;
      const before = textarea.value.substring(0, start);
      const after  = textarea.value.substring(end);
      const newValue = before + toInsert + after;

      // Use onInsert to update React state
      onInsert(newValue, start + toInsert.length);

      // Restore cursor position after React re-render
      setTimeout(() => {
        if (textarea) {
          textarea.focus();
          textarea.selectionStart = start + toInsert.length;
          textarea.selectionEnd   = start + toInsert.length;
        }
      }, 10);
    } else {
      onInsert(toInsert);
    }
  };

  return (
    <div style={S.panel}>
      {/* ── Header ── */}
      <div style={S.header}>
        <span style={S.headerTitle}>∑ Math Keyboard</span>
        <div style={S.headerRight}>
          <label style={S.wrapLabel} title="Wrap symbols in $ for math rendering">
            <input
              type="checkbox"
              checked={wrapInDollars}
              onChange={e => setWrapInDollars(e.target.checked)}
              style={{ marginRight: '4px' }}
            />
            Auto $
          </label>
          <button style={S.closeBtn} onClick={onClose} title="Close math keyboard">
            ✕
          </button>
        </div>
      </div>

      {/* ── Category tabs ── */}
      <div style={S.tabs}>
        {SYMBOL_CATEGORIES.map(cat => (
          <button
            key={cat.name}
            style={S.tab(activeCategory === cat.name)}
            onClick={() => setActiveCategory(cat.name)}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* ── Symbol grid ── */}
      <div style={S.grid}>
        {activeSymbols.map((symbol, i) => (
          <button
            key={i}
            style={S.symbolBtn}
            onClick={() => handleInsert(symbol)}
            title={symbol.title}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = BG_H; e.currentTarget.style.borderColor = ACCENT; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.borderColor = BORDER; }}
          >
            {symbol.label}
          </button>
        ))}
      </div>

      {/* ── Tip ── */}
      <div style={S.tip}>
        Tip: symbols wrapped in $ render as formatted math in the conversation.
        Use $$ on a new line for display equations.
      </div>
    </div>
  );
}

const S = {
  panel: {
    borderTop: `1px solid ${BORDER}`,
    backgroundColor: BG,
    flexShrink: 0,
    userSelect: 'none',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 12px',
    backgroundColor: BG_H,
    borderBottom: `1px solid ${BORDER}`,
  },
  headerTitle: {
    fontSize: '12px',
    fontWeight: '700',
    color: ACCENT,
    fontFamily: FONT,
    letterSpacing: '0.5px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  wrapLabel: {
    fontSize: '11px',
    color: TEXT_MID,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    fontFamily: FONT,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: TEXT_MID,
    fontSize: '14px',
    padding: '2px 4px',
    lineHeight: 1,
  },
  tabs: {
    display: 'flex',
    gap: '2px',
    padding: '6px 8px 4px',
    flexWrap: 'wrap',
    borderBottom: `1px solid ${BORDER}`,
  },
  tab: (active) => ({
    padding: '3px 8px',
    fontSize: '10px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    border: `1px solid ${active ? ACCENT : BORDER}`,
    borderRadius: '3px',
    cursor: 'pointer',
    backgroundColor: active ? ACCENT : 'transparent',
    color: active ? '#fff' : TEXT_MID,
    fontWeight: active ? '600' : '400',
    fontFamily: FONT,
  }),
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    padding: '8px 10px',
  },
  symbolBtn: {
    minWidth: '40px',
    height: '36px',
    padding: '0 6px',
    border: `1px solid ${BORDER}`,
    borderRadius: '4px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: '16px',
    color: TEXT_H,
    fontFamily: 'serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.1s',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },
  tip: {
    fontSize: '10px',
    color: TEXT_MID,
    padding: '4px 12px 6px',
    fontFamily: FONT,
    fontStyle: 'italic',
  },
};
